import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { LEAD_FORM_COLOR_PALETTE } from '../../../lib/crm/leadForms';

export type LeadFormColorTarget = 'header' | 'background' | 'text' | 'button';

type Props = {
  target: LeadFormColorTarget;
  currentColor: string;
  onClose: () => void;
  onSelect: (color: string) => void;
};

const TITLES: Record<LeadFormColorTarget, string> = {
  header: 'Header color',
  background: 'Background color',
  text: 'Text color',
  button: 'Button color',
};

export default function LeadFormColorPickerModal({
  target,
  currentColor,
  onClose,
  onSelect,
}: Props) {
  return createPortal(
    <div
      className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div
        className="w-full max-w-sm bg-white rounded-t-[16px] sm:rounded-[16px] shadow-xl p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[16px] font-semibold text-gray-900">{TITLES[target]}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-[10px] active:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-2 mb-4">
          {LEAD_FORM_COLOR_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => onSelect(color)}
              className={`h-9 w-9 rounded-full border-2 touch-manipulation ${
                currentColor.toLowerCase() === color.toLowerCase()
                  ? 'border-blue-600 scale-110'
                  : 'border-gray-200'
              }`}
              style={{ backgroundColor: color, WebkitTapHighlightColor: 'transparent' }}
              aria-label={color}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={currentColor}
            onChange={(e) => onSelect(e.target.value)}
            className="h-10 w-14 rounded border border-gray-200 cursor-pointer"
          />
          <input
            type="text"
            value={currentColor}
            onChange={(e) => onSelect(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-[10px] text-[14px] font-mono"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
